import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getViewSlice, parsePersistedPagination, setViewSlice } from '../../sessionStorage/viewState';

/**
 * Extra page only when loaded items fill complete pages and Firestore may have more.
 * Avoids growing 3 → 4 → 5 pages on every next() while still showing one speculative page.
 */
/**
 * How long a restore waits on a source that reports no more data before
 * accepting the page is gone. Longer than the SecondaryPanel filter debounce
 * (500ms), which is what makes the source restart its batches after mount.
 */
const RESTORE_SETTLE_DELAY_MS = 1000;

/**
 * Safety net for the "still filling the page" loading state below: a page that
 * keeps taking rounds without ever filling stops holding the view hostage. Only
 * bounds rounds that actually happen — see FILL_SETTLE_DELAY_MS for the rounds
 * that never happen at all.
 */
const MAX_FILL_ROUNDS = 5;

/**
 * How long the fill loading state waits on a round that produces nothing before
 * accepting that no more rows are coming.
 *
 * `gotNewData` means "the last batch came back full", so a source whose batches
 * are all exhausted still reports it while its `next()` no-ops. A no-op next()
 * yields no data, no state change and therefore no re-render, so the round
 * counter never advances and nothing else would ever end the loading state.
 * Same reasoning (and the same 500ms filter debounce) as RESTORE_SETTLE_DELAY_MS.
 */
const FILL_SETTLE_DELAY_MS = 1000;

export const getEstimatedFirestoreTotal = ({
  totalItems,
  pageSize,
  gotNewData,
  loading,
}) => {
  if (!gotNewData || loading || totalItems < pageSize) {
    return totalItems;
  }
  if (totalItems % pageSize === 0) {
    return totalItems + pageSize;
  }
  return totalItems;
};

/**
 * Hook for handling pagination with Firestore data loaded progressively.
 * Works with useFirestoreQueryPagination, useFirestoreRealtimeBatchedPagination,
 * or similar hooks that provide next() and gotNewData.
 *
 * @param {Array} data - The data array from Firestore hook
 * @param {Object} firestoreHook - Object containing { gotNewData, loading, next }
 * @param {number} pageSize - Items per page (default: 10)
 * @param {Object} filters - Filters object to watch for changes (optional)
 * @param {Object} options
 * @param {boolean} options.fillIncompletePage - If true, call next() until the
 *   current page is full or gotNewData is false. Use when gotNewData means
 *   "last Firestore page was full" (realtime batched pagination). Keep false
 *   when data is filtered on the client (Jobs, Shifts, Divisions).
 * @param {string} [options.viewStorageKey] - Per-view sessionStorage key. When
 *   set, persist/restore the `pagination` slice (`page` and/or `pageSize`).
 */
const usePaginationWithFirestore = (
  data = [],
  { gotNewData = false, loading = false, next = null } = {},
  initialPageSize = 10,
  filters = {},
  { fillIncompletePage = false, viewStorageKey = null } = {},
) => {
  const [currentPage, setCurrentPage] = useState(() => {
    if (!viewStorageKey) return 1;
    return parsePersistedPagination(getViewSlice(viewStorageKey, 'pagination')).page ?? 1;
  });
  // pageSize is owned here so a runtime size changer has a single source of
  // truth: the component reads it back from paginationState and drives changes
  // through handlePageChange. `initialPageSize` only seeds it.
  const [pageSize, setPageSize] = useState(() => {
    if (!viewStorageKey) return initialPageSize;
    return (
      parsePersistedPagination(getViewSlice(viewStorageKey, 'pagination')).pageSize ??
      initialPageSize
    );
  });
  /**
   * Persist intent only: a page the user chose, or the page 1 a filter change
   * forces. Range corrections (the clamp effect below) are deliberately not
   * persisted — the loaded window shrinks transiently every time the source
   * resets its batches, and writing the clamped page there would destroy the
   * stored page for good: the next visit would restore the clamp instead of the
   * page the user actually chose.
   */
  const persistPagination = useCallback(
    (page, size) => {
      if (!viewStorageKey) return;

      const toPositiveInt = value => {
        const parsed = Number(value);
        return Number.isInteger(parsed) && parsed >= 1 ? parsed : undefined;
      };

      const payload = {};
      const nextPage = toPositiveInt(page);
      const nextSize = toPositiveInt(size);
      if (nextPage != null) payload.page = nextPage;
      if (nextSize != null) payload.pageSize = nextSize;
      if (Object.keys(payload).length === 0) return;

      setViewSlice(viewStorageKey, 'pagination', payload);
    },
    [viewStorageKey],
  );
  /**
   * Page the user left behind, held until the loaded data can show it (or the
   * user moves somewhere else). While it is set the view stays on that page and
   * reports `isRestoringPage`, so the list renders its loading state instead of
   * painting page 1 and jumping a few milliseconds later.
   */
  const [restoreTarget, setRestoreTarget] = useState(() => {
    if (!viewStorageKey) return null;
    const { page } = parsePersistedPagination(getViewSlice(viewStorageKey, 'pagination'));
    return page > 1 ? page : null;
  });
  /** The restore gave up: the clamp below may correct that page, and only that one. */
  const abandonedRestoreRef = useRef(false);
  /**
   * Fill rounds spent on the current page/filters. Only bounds the loading state
   * (see `isFillingPage`), never the fetching itself. State, not a ref: the
   * render reads it to decide whether the fill has given up.
   */
  const [fillRounds, setFillRounds] = useState(0);
  /** The fill gave up waiting: the page is shown as it stands. */
  const [isFillStalled, setIsFillStalled] = useState(false);
  const totalItems = data.length;
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = data.slice(startIndex, startIndex + pageSize);

  const estimatedTotal = useMemo(() => {
    // In fillIncompletePage mode we prefetch exactly one page ahead (see the
    // probe effect below), so `totalItems` already includes the next page
    // whenever it exists. Reporting the loaded count as-is — with no speculative
    // "+ pageSize" — is what keeps a trailing empty page from ever rendering:
    // the page count grows only as real data arrives and stops the moment a
    // probe confirms the end. Non-prefetch consumers keep the speculative page
    // so their "next" control stays reachable without prefetching.
    if (fillIncompletePage) {
      return totalItems;
    }
    return getEstimatedFirestoreTotal({
      totalItems,
      pageSize,
      gotNewData,
      loading,
    });
  }, [fillIncompletePage, totalItems, pageSize, gotNewData, loading]);

  const loadNext = useCallback(async () => {
    if (!next) {
      return;
    }

    try {
      await next();
    } catch (error) {
      console.error('usePaginationWithFirestore: Error loading more data:', error);
    }
  }, [next]);

  const fetchIfNeeded = useCallback(
    async page => {
      if (!gotNewData || loading || !next) {
        return;
      }

      const needsMore = fillIncompletePage
        ? totalItems < page * pageSize
        : page > Math.ceil(totalItems / pageSize);

      if (!needsMore) {
        return;
      }

      await loadNext();
    },
    [fillIncompletePage, gotNewData, loading, loadNext, next, pageSize, totalItems],
  );

  const handlePageChange = useCallback(
    async (page, nextPageSize) => {
      setRestoreTarget(null);
      setFillRounds(0);
      setIsFillStalled(false);
      // antd Pagination's onChange fires with (page, pageSize) for BOTH page and
      // size changes. On a size change, keep the first visible row in view and
      // let the fill/probe effects fetch whatever the new page size needs.
      if (nextPageSize && nextPageSize !== pageSize) {
        const firstItemIndex = (currentPage - 1) * pageSize;
        const nextPage = Math.floor(firstItemIndex / nextPageSize) + 1;
        setPageSize(nextPageSize);
        setCurrentPage(nextPage);
        persistPagination(nextPage, nextPageSize);
        return;
      }
      setCurrentPage(page);
      persistPagination(page, pageSize);
      await fetchIfNeeded(page);
    },
    [currentPage, pageSize, fetchIfNeeded, persistPagination],
  );

  /**
   * The current page is short of rows and the source still claims to have more:
   * a fill round is either running or about to run.
   */
  const isFillPending =
    fillIncompletePage && !!next && gotNewData && totalItems > 0 && paginatedData.length < pageSize;

  useEffect(() => {
    if (!fillIncompletePage) {
      return;
    }
    if (paginatedData.length < pageSize && gotNewData && !loading && next && totalItems > 0) {
      setFillRounds(rounds => rounds + 1);
      loadNext();
    }
  }, [
    fillIncompletePage,
    paginatedData.length,
    pageSize,
    gotNewData,
    loading,
    next,
    totalItems,
    loadNext,
  ]);

  /**
   * fillIncompletePage mode — resolve the speculative ("+ pageSize") page at the
   * real end of the data.
   *
   * `gotNewData` means "the last Firestore page came back full", which is a
   * false positive whenever the total is an exact multiple of pageSize: after
   * loading e.g. 10/2 items the last batch is full, so gotNewData stays true and
   * `getEstimatedFirestoreTotal` speculates a 6th (empty) page. Firestore can't
   * tell "exactly 10" from "10 + more" without one more read, so while we sit on
   * the last loaded full page we probe the next one. An empty/short probe flips
   * gotNewData to false and the phantom page disappears instead of rendering
   * empty. A full probe just prefetches one page ahead (bounded: totalItems
   * grows, so the condition stops firing).
   *
   * Used by lists whose data source keeps the "last batch was full" semantics
   * (UsersList, LocationAlerts). Lists whose source over-fetches one row to make
   * gotNewData exact (see useCollectionWithSubcollectionsPaginated's peek) need
   * neither this probe nor a speculative page, so they leave this option off.
   */
  const loadedPages = Math.ceil(totalItems / pageSize);
  useEffect(() => {
    if (!fillIncompletePage) {
      return;
    }
    if (
      gotNewData &&
      !loading &&
      next &&
      totalItems > 0 &&
      totalItems % pageSize === 0 &&
      currentPage >= loadedPages
    ) {
      loadNext();
    }
  }, [
    fillIncompletePage,
    gotNewData,
    loading,
    next,
    totalItems,
    pageSize,
    currentPage,
    loadedPages,
    loadNext,
  ]);

  /**
   * A fill round that delivers nothing produces no re-render to react to, so the
   * wait is bounded by a timer instead of another probe (same shape as the page
   * restore above). Every render where the source is fetching or the loaded
   * count grew restarts it, so real progress is never cut short — only a fill
   * that has genuinely stopped moving stops being reported as loading.
   */
  useEffect(() => {
    if (!isFillPending || isFillStalled) {
      return undefined;
    }

    const timeoutId = setTimeout(() => setIsFillStalled(true), FILL_SETTLE_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [isFillPending, isFillStalled, loading, totalItems]);

  useEffect(() => {
    if (isFillStalled && !isFillPending) {
      setIsFillStalled(false);
    }
  }, [isFillPending, isFillStalled]);

  const resetPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const filtersString = JSON.stringify(filters);
  const prevFiltersRef = useRef(filtersString);

  useEffect(() => {
    if (prevFiltersRef.current === filtersString) {
      return;
    }
    prevFiltersRef.current = filtersString;
    // A real filter change outranks a restored page. Callers must keep their
    // filters stable while the panel hydrates (see isFilterPanelHydrated),
    // otherwise that hydration reads as a change and drops the restored page.
    setRestoreTarget(null);
    abandonedRestoreRef.current = false;
    setFillRounds(0);
    setIsFillStalled(false);
    resetPage();
    persistPagination(1, pageSize);
  }, [filtersString, resetPage, persistPagination, pageSize]);

  /**
   * A restored page > 1 has no rows until the source streams enough batches, so
   * keep pulling while it reports more data. The view stays on the target the
   * whole time (`isRestoringPage` drives the list's loading state), so the user
   * never sees page 1 painted and replaced a few milliseconds later.
   *
   * "No more data" is not proof that the page is gone: the source restarts its
   * batches on every dependency change, and the filter panel settles ~500ms
   * after mount. `getNextFetch` no-ops once no batch has more, which produces no
   * re-render to react to, so the wait is bounded by a timer instead of another
   * probe: if nothing new lands, the page really is gone and the clamp below
   * corrects the view.
   */
  useEffect(() => {
    if (restoreTarget == null) return undefined;

    if (totalItems > (restoreTarget - 1) * pageSize) {
      setRestoreTarget(null);
      if (currentPage !== restoreTarget) {
        setCurrentPage(restoreTarget);
      }
      return undefined;
    }

    if (loading) return undefined;

    if (gotNewData && next) {
      loadNext();
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      abandonedRestoreRef.current = true;
      setRestoreTarget(null);
    }, RESTORE_SETTLE_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [currentPage, gotNewData, loading, loadNext, next, pageSize, restoreTarget, totalItems]);

  /**
   * Range correction for an abandoned restore only. A page the caller asked for
   * is left alone: lists sit on the speculative page of
   * `getEstimatedFirestoreTotal` while the next batch loads, and correcting that
   * would undo the navigation the user just did.
   */
  useEffect(() => {
    if (restoreTarget != null || !abandonedRestoreRef.current) return;
    if (gotNewData || loading || totalItems === 0) return;
    const lastPage = Math.max(1, Math.ceil(totalItems / pageSize));
    if (currentPage > lastPage) {
      // Range correction, not user intent — see persistPagination: not stored.
      setCurrentPage(lastPage);
    }
  }, [gotNewData, loading, totalItems, pageSize, currentPage, restoreTarget]);

  const isRestoringPage = restoreTarget != null && paginatedData.length === 0;

  /**
   * fillIncompletePage mode — the page is not settled until it is full or the
   * source runs out of data. The source reports `loading: false` for one frame
   * between fill rounds, and surfacing that toggles the list's spinner once per
   * round (spinner → rows → spinner → rows), which reads as "it loads in
   * batches". Reporting the whole fill cycle as loading keeps one single
   * loading state for one user interaction.
   */
  const isFillGivenUp = isFillStalled || fillRounds >= MAX_FILL_ROUNDS;
  const isFillingPage = isFillPending && !isFillGivenUp;

  /**
   * A fetch that runs while the current page is already full is a background
   * prefetch (the end-of-data probe below, or a realtime update): the rows on
   * screen are valid, so it must not block the view. The same holds once a fill
   * has given up — the page is shown as it stands, and blocking it on every
   * later round would only bring the flicker back in slow motion. Only a fetch
   * the page is still genuinely waiting on is worth a loading state.
   */
  const isBackgroundFetch = paginatedData.length >= pageSize || (isFillPending && isFillGivenUp);
  const isLoading = isRestoringPage || isFillingPage || (loading && !isBackgroundFetch);

  return {
    currentPage,
    pageSize,
    totalItems,
    estimatedTotal,
    paginatedData,

    handlePageChange,
    resetPage,

    // Single loading state for the whole "make this page showable" cycle —
    // source fetch + page restore + fill rounds. See single-loading-indicator.
    loading: isLoading,
    /** Raw source flag, for callers that need the fetch itself. */
    isFetching: loading,
    isFillingPage,
    // Merge into the list's single loading indicator: while true the view sits
    // on the stored page with no rows yet, and rendering that as an empty table
    // (or letting the pager be clicked) is what the loading state is for.
    isRestoringPage,
    hasMoreData: gotNewData,
  };
};

export default usePaginationWithFirestore;
